import { toast } from "react-toastify";

import { AxiosError } from "axios";

export interface ValidationErrorNode {
  rule: string;
  field: string;
  message: string;
}

export interface ValidationErrorResponse {
  error: {
    message: string;
    nodes: ValidationErrorNode[];
  };
}

export interface ApiErrorResponse {
  message?: string;
  error?: {
    message?: string;
    nodes?: ValidationErrorNode[];
  };
}

/**
 * Field name to user-friendly French labels mapping
 */
const FIELD_LABELS: Record<string, string> = {
  email: "Email",
  firstname: "Prénom",
  lastname: "Nom",
  phone: "Téléphone",
  password: "Mot de passe",
  username: "Nom d'utilisateur",
  label: "Libellé",
  name: "Nom",
  title: "Titre",
  address: "Adresse",
  zip: "Code postal",
  city: "Ville",
  company: "Entreprise",
  description: "Description",
  // Add more field mappings as needed
};

/**
 * Rule to user-friendly French messages mapping
 */
const RULE_MESSAGES: Record<string, (field: string) => string> = {
  unique: (field: string) => `${field} est déjà utilisé`,
  required: (field: string) => `${field} est obligatoire`,
  email: (field: string) => `${field} doit être un email valide`,
  minLength: (field: string) => `${field} est trop court`,
  maxLength: (field: string) => `${field} est trop long`,
  min: (field: string) => `${field} est trop petit`,
  max: (field: string) => `${field} est trop grand`,
  pattern: (field: string) => `${field} n'est pas au bon format`,
  exists: (field: string) => `${field} n'existe pas`,
  // Add more rule mappings as needed
};

/**
 * Get user-friendly field label
 */
export const getFieldLabel = (field: string): string => {
  return FIELD_LABELS[field] || field;
};

/**
 * Get user-friendly error message for a validation rule
 */
export const getValidationMessage = (rule: string, field: string): string => {
  const fieldLabel = getFieldLabel(field);
  const messageGenerator = RULE_MESSAGES[rule];
  
  if (messageGenerator) {
    return messageGenerator(fieldLabel);
  }
  
  // Fallback message
  return `Erreur de validation pour ${fieldLabel}`;
};

/**
 * Check if an error is a validation error with nodes
 */
export const isValidationError = (error: AxiosError): boolean => {
  const data = error.response?.data as ApiErrorResponse;
  return !!(
    data?.error?.message?.includes("E_VALIDATION_FAILURE") &&
    data?.error?.nodes &&
    Array.isArray(data.error.nodes)
  );
};

/**
 * Parse validation error and show appropriate toast messages
 */
export const handleValidationError = (error: AxiosError): void => {
  const data = error.response?.data as ValidationErrorResponse;
  
  if (!isValidationError(error) || !data?.error?.nodes) {
    return;
  }

  // Show a toast for each validation error
  data.error.nodes.forEach((node: ValidationErrorNode) => {
    const message = getValidationMessage(node.rule, node.field);
    toast.error(message);
  });
};

/**
 * Generic API error handler that handles validation errors and other API errors
 */
export const handleApiError = (error: AxiosError, fallbackMessage?: string): void => {
  if (isValidationError(error)) {
    handleValidationError(error);
    return;
  }

  // Handle other API errors
  const data = error.response?.data as ApiErrorResponse;
  const errorMessage = 
    data?.error?.message || 
    data?.message || 
    fallbackMessage || 
    "Une erreur inattendue s'est produite";

  toast.error(errorMessage);
};

/**
 * Enhanced error handler for React Query mutations
 * Provides better error messages and handling for validation errors
 */
export const createMutationErrorHandler = (fallbackMessage?: string) => {
  return (error: AxiosError) => {
    console.error("Mutation error:", error);
    handleApiError(error, fallbackMessage);
  };
};
